/**
 * @fileoverview Job Timesheets Page
 *
 * URL: /dashboard/jobs/:jobId/timesheets
 *
 * The timesheet surface embedded in a single job, locked to that matter.
 * Everyone on the job records their own time here (Entries). Approvers
 * additionally get an Approvals tab — the review queue scoped to this matter.
 *
 * When the caller is not an approver there is only one surface, so the tab
 * bar is dropped and the weekly timesheet renders on its own.
 *
 * The approver gate mirrors the firm-wide approvals page and is enforced
 * server-side; the tab simply hides what the caller cannot use.
 *
 * Above both sits the job's rate badge (MOR-1038): which rate card the matter
 * is charged on and whether its rates are frozen, with a Change button for
 * org admins and owners.
 *
 * Dependencies:
 * - Convex: timesheets.settings.getForOrg / entries.approvalAccess /
 *   entries.approvalQueueCount / jobRates.getForJob
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { useActiveOrg } from "@/utils/org-context";
import { Badge } from "@/ui/primitives";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { WeeklyTimesheet } from "@/components/timesheets/WeeklyTimesheet";
import { ApprovalsQueue } from "@/components/timesheets/ApprovalsQueue";
import { JobRateBadge } from "@/components/timesheets/JobRateBadge";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/timesheets",
)({
  beforeLoad: () => ({ headerTitle: "Timesheets" }),
  component: JobTimesheetsPage,
});

function JobTimesheetsPage() {
  const { jobId } = Route.useParams();
  const { orgId } = useActiveOrg();
  const [tab, setTab] = useState("entries");

  const { data: settings } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.settings.getForOrg,
      orgId ? { orgId } : undefined,
    ),
    enabled: !!orgId,
  });
  const { data: canApprove } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.approvalAccess,
      orgId ? { orgId } : undefined,
    ),
    enabled: !!orgId,
  });
  const { data: awaitingCount } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.approvalQueueCount,
      orgId ? { orgId, jobId: jobId as Id<"jobs"> } : undefined,
    ),
    enabled: !!orgId && canApprove === true,
  });

  if (!orgId) {
    return (
      <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
        <div className="text-sm text-muted-foreground">
          Select an organisation to record time.
        </div>
      </div>
    );
  }

  // Non-approvers only have the Entries surface, so skip the tab chrome.
  if (canApprove !== true) {
    return (
      <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
        <JobRateBadge
          orgId={orgId as Id<"organizations">}
          jobId={jobId as Id<"jobs">}
        />
        <WeeklyTimesheet
          orgId={orgId as Id<"organizations">}
          jobId={jobId as Id<"jobs">}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <JobRateBadge
        orgId={orgId as Id<"organizations">}
        jobId={jobId as Id<"jobs">}
      />
      <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="entries">Entries</TabsTrigger>
          <TabsTrigger value="approvals" className="gap-2">
            Approvals
            {(awaitingCount ?? 0) > 0 && (
              <Badge size="sm" color="amber" variant="soft">
                {awaitingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="entries">
          <WeeklyTimesheet
            orgId={orgId as Id<"organizations">}
            jobId={jobId as Id<"jobs">}
          />
        </TabsContent>

        <TabsContent value="approvals">
          <ApprovalsQueue
            orgId={orgId as Id<"organizations">}
            unitMinutes={settings?.unitMinutes ?? 6}
            roundingMode={settings?.roundingMode ?? "up"}
            jobId={jobId as Id<"jobs">}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
